import type { AppDispatch } from 'src/store';
import type { NavigationState } from '@skynet/data-navigation';
import { fetchNavigationPermissions } from '@skynet/authz-navigation';
import { navigationPermissionsActions } from '@skynet/data-navigation';
import { uiNavigationActions } from 'src/routes/navigation-config';
import type { Principal } from '@skynet/authz-provider';
import { CerbosClient, getPrincipalKey } from '@skynet/authz-provider';

/**
 * Fetches navigation permissions and dispatches them to the store.
 * Use this helper to avoid duplicating the fetch + dispatch logic.
 *
 * @param getState - Returns the current store state; used to detect a logout that
 *   races with an in-flight Cerbos request. If the store is reset while the fetch is
 *   in-flight, the stale result is discarded instead of being written back into Redux.
 *   Concurrent calls for *different* users are handled upstream: root-middleware uses
 *   the stored principalKey to decide whether to use the cache or re-fetch.
 */
export const fetchAndSetNavigationPermissions = async (
  dispatch: AppDispatch,
  principal: Principal,
  getState: () => { navigationPermissions: NavigationState },
): Promise<string[]> => {
  dispatch(navigationPermissionsActions.setLoading('pending'));
  const allowedActions = await fetchNavigationPermissions(
    CerbosClient,
    principal,
    uiNavigationActions,
  );

  // If the store was reset (logout) while the Cerbos request was in-flight,
  // isLoading will no longer be 'pending'. Discard the stale result to prevent
  // the previous user's permissions from contaminating the next session.
  if (getState().navigationPermissions.isLoading !== 'pending') {
    return [];
  }

  const sorted = [...allowedActions].sort(
    (a, z) => uiNavigationActions.indexOf(a) - uiNavigationActions.indexOf(z),
  );
  dispatch(
    navigationPermissionsActions.setData({
      data: sorted,
      principalKey: getPrincipalKey(principal),
    }),
  );
  return sorted;
};
